<template>
    <div class="mt-[4rem]  pb-[2.1rem]  ">
      <div class="pt-[2.188rem]">
            <!-- Header -->
            <div class="text-center ">
                <h1 class="text-4xl font-bold text-[#212121] mb-4">{{ $t('contactUs') }}</h1>
                <p class="text-lg text-[#757575] max-w-2xl mx-auto">
                    {{ $t('contactDesc') }}
                </p>
            </div>

            <!-- Grid principal avec 2 colonnes -->
            <div class="bg-white rounded-lg shadow-md p-6">
                <h2 class="text-2xl font-semibold text-[#212121] mb-6 flex items-center gap-3">
                    <i class="pi pi-send text-[#00798C]"></i>
                    {{ $t('sendMessage') }}
                </h2>

                <form @submit.prevent="sendMessage" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    <!-- Colonne 1: Informations de contact et champs du formulaire -->
                    <div class="space-y-6">
                        <!-- Informations de contact -->
                        <div class="bg-[#F8F9FA] rounded-lg p-4">
                            <h3 class="text-lg font-semibold text-[#212121] mb-4 flex items-center gap-2">
                                <i class="pi pi-info-circle text-[#00798C]"></i>
                                {{ $t('contactInformation') }}
                            </h3>

                            <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <!-- Email -->
                                <div class="flex items-start gap-3">
                                    <i class="pi pi-envelope text-[#00798C] text-lg flex-shrink-0 mt-1"></i>
                                    <div>
                                        <h4 class="font-semibold text-[#212121] text-sm">{{ $t('email') }}</h4>
                                        <p class="text-[#757575] text-sm">admin@company.com</p>
                                        <p class="text-xs text-[#757575]">{{ $t('responseTime') }}</p>
                                    </div>
                                </div>

                                <!-- Telephone -->
                                <div class="flex items-start gap-3">
                                    <i class="pi pi-phone text-[#0FAA58] text-lg flex-shrink-0 mt-1"></i>
                                    <div>
                                        <h4 class="font-semibold text-[#212121] text-sm">{{ $t('phone') }}</h4>
                                        <p class="text-[#757575] text-sm">+33 1 23 45 67 89</p>
                                        <p class="text-xs text-[#757575]">{{ $t('workingHours') }}</p>
                                    </div>
                                </div>

                                <!-- Support technique -->
                                <div class="flex items-start gap-3">
                                    <i class="pi pi-cog text-[#58B6B8] text-lg flex-shrink-0 mt-1"></i>
                                    <div>
                                        <h4 class="font-semibold text-[#212121] text-sm">{{ $t('techSupport') }}</h4>
                                        <p class="text-[#757575] text-sm">support@company.com</p>
                                        <p class="text-xs text-[#757575]">{{ $t('support24_7') }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Sujet -->
                        <div class="field-group">
                            <FloatLabel variant="on" class="w-full">
                                <Select autoFilterFocus id="subject" v-model="contactForm.subject"
                                    :options="subjectOptions" optionLabel="label" optionValue="value"
                                    :invalid="v$.contactForm.subject.$error" class="w-full" />
                                <label for="subject">{{ $t('subject') }} *</label>
                            </FloatLabel>
                            <div v-if="v$.contactForm.subject.$error" class="error-message">
                                {{ v$.contactForm.subject.$errors[0].$message }}
                            </div>
                        </div>

                        <!-- Nom de l'expediteur -->
                        <div class="field-group">
                            <FloatLabel variant="on" class="w-full">
                                <InputText id="senderName" v-model="contactForm.senderName"
                                    :invalid="v$.contactForm.senderName.$error" class="w-full" />
                                <label for="senderName">{{ $t('yourName') }} *</label>
                            </FloatLabel>
                            <div v-if="v$.contactForm.senderName.$error" class="error-message">
                                {{ v$.contactForm.senderName.$errors[0].$message }}
                            </div>
                        </div>

                        <!-- Priorite -->
                        <div class="field-group">
                            <label class="block text-sm font-medium text-[#757575] mb-3">{{ $t('priority') }}</label>
                            <div class="flex gap-4 flex-wrap">
                                <div class="flex items-center">
                                    <RadioButton id="priority-low" name="priority" v-model="contactForm.priority" value="low" />
                                    <label for="priority-low" class="ml-2 text-[#28a745]">{{ $t('low') }}</label>
                                </div>
                                <div class="flex items-center">
                                    <RadioButton id="priority-normal" name="priority" v-model="contactForm.priority" value="normal" />
                                    <label for="priority-normal" class="ml-2 text-[#fd7e14]">{{ $t('normal') }}</label>
                                </div>
                                <div class="flex items-center">
                                    <RadioButton id="priority-urgent" name="priority" v-model="contactForm.priority" value="urgent" />
                                    <label for="priority-urgent" class="ml-2 text-[#dc3545]">{{ $t('urgent') }}</label>
                                </div>
                            </div>
                        </div>


                        <!-- Consentement -->
                        <div class="field-group">
                            <div class="flex items-start gap-3">
                                <Checkbox id="consent" v-model="contactForm.consent" binary
                                    :invalid="v$.contactForm.consent.$error" />
                                <label for="consent" class="text-sm text-[#757575] leading-relaxed">
                                    {{ $t('consent') }}
                                </label>
                            </div>
                            <div v-if="v$.contactForm.consent.$error" class="error-message">
                                {{ v$.contactForm.consent.$errors[0].$message }}
                            </div>
                        </div>
                    </div>

                    <!-- Colonne 2: Zone de saisie du message (plus large) -->
                    <div class="space-y-6">
                        <!-- Message avec editeur de texte riche -->
                        <div class="field-group">
                            <label class="block text-sm font-medium text-[#757575] mb-2">{{ $t('message') }} *</label>
                            <Editor v-model="contactForm.message" editorStyle="height: 400px"
                                :invalid="v$.contactForm.message.$error" class="w-full"/>
                                
                            <div v-if="v$.contactForm.message.$error" class="error-message">
                                {{ v$.contactForm.message.$errors[0].$message }}
                            </div>
                            <div class="text-right text-sm text-[#757575] mt-1">
                                {{ getTextLength(contactForm.message) }}/5000 {{ $t('characters') }}
                            </div>
                        </div>
                    </div>

                    <!-- Boutons sur toute la largeur (colspan 2) -->
                    <div class="lg:col-span-2 flex justify-end gap-4 pt-4 border-t border-[#DEE0E4]">
                        <Button type="button" :label="$t('cancel')" icon="pi pi-refresh" class="p-button-outlined"
                            @click="resetForm" />
                        <Button type="submit" :label="$t('sendMessageBtn')" icon="pi pi-send" :loading="loading"
                            class="min-w-[200px]" />
                    </div>
                </form>
            </div>

            <!-- Section d'aide rapide -->
            <div class="mt-12">
                <h2 class="text-2xl font-semibold text-[#212121] mb-6 text-center">{{ $t('needHelp') }}</h2>
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div class="bg-white rounded-lg shadow-md p-6 text-center">
                        <div
                            class="w-16 h-16 bg-[#00798C] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i class="pi pi-book text-[#00798C] text-2xl"></i>
                        </div>
                        <h3 class="font-semibold text-[#212121] mb-2">{{ $t('documentation') }}</h3>
                        <p class="text-[#757575] text-sm mb-4">{{ $t('documentationDesc') }}</p>
                        <Button :label="$t('viewDocumentation')" class="p-button-outlined p-button-sm" />
                    </div>

                    <div class="bg-white rounded-lg shadow-md p-6 text-center">
                        <div
                            class="w-16 h-16 bg-[#0FAA58] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i class="pi pi-video text-[#0FAA58] text-2xl"></i>
                        </div>
                        <h3 class="font-semibold text-[#212121] mb-2">{{ $t('videoTutorials') }}</h3>
                        <p class="text-[#757575] text-sm mb-4">{{ $t('videoTutorialsDesc') }}</p>
                        <Button :label="$t('watchVideos')" class="p-button-outlined p-button-sm" />
                    </div>

                    <div class="bg-white rounded-lg shadow-md p-6 text-center">
                        <div
                            class="w-16 h-16 bg-[#58B6B8] bg-opacity-10 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i class="pi pi-comments text-[#58B6B8] text-2xl"></i>
                        </div>
                        <h3 class="font-semibold text-[#212121] mb-2">{{ $t('liveChat') }}</h3>
                        <p class="text-[#757575] text-sm mb-4">{{ $t('liveChatDesc') }}</p>
                        <Button :label="$t('startChat')" class="p-button-outlined p-button-sm" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength } from '@vuelidate/validators';
import { reactive } from 'vue';
import { defineComponent } from 'vue';
import Checkbox from 'primevue/checkbox';
import RadioButton from 'primevue/radiobutton';
import { sendContactMessage } from '~/composables/contact';


interface ContactForm {
    subject: string;
    priority: string;
    message: string;
    consent: boolean;
    senderName: string;
}

export default defineComponent({
    name: "ContactPage",
    components: { Checkbox, RadioButton },
    setup() {
        // Configuration de la page Nuxt
        definePageMeta({
            title: 'Contact',
            layout: 'default',
            middleware: ['auth']
        });

        const contactForm = reactive<ContactForm>({
            subject: '',
            priority: 'normal',
            message: '',
            consent: false,
            senderName: ''
        });

        const rules = {
            contactForm: {
                subject: { required },
                message: { required, maxLength: maxLength(5000) },
                consent: { required },
                senderName: { required }
            }
        };

        const v$ = useVuelidate(rules, { contactForm });

        return { contactForm, v$ };
    },
    data() {
        return {
            loading: false,
            subjectOptions: [
                { label: 'Question generale', value: 'general' },
                { label: 'Probleme technique', value: 'technical' },
                { label: 'Demande de fonctionnalite', value: 'feature' },
                { label: 'Bug ou erreur', value: 'bug' },
                { label: 'Probleme de compte', value: 'account' },
                { label: 'Facturation', value: 'billing' },
                { label: 'Autre', value: 'other' }
            ]
        };
    },
    mounted() {
        // Configuration de la page
        this.$router.currentRoute.value.meta.title = 'Contact';
    },
    methods: {
        async sendMessage() {
            const isValid = await this.v$.$validate();
            if (!isValid) {
                ErrorAlert({message: "Veuillez corriger les erreurs dans le formulaire avant de l'envoyer."});
                return;
            }

            this.loading = true;

            try {
                // Preparer les donnees du formulaire
               
                // Envoyer a l'API
                const response = await sendContactMessage(this.contactForm);
                    SuccessAllertWithoutReload("Votre message a ete envoye avec succes ! Nous vous repondrons dans les plus brefs delais.");
                    this.resetForm();
            } catch (error) {
                ErrorAlert(error);
            } finally {
                this.loading = false;
            }
        },

        resetForm() {
            // Reinitialiser le formulaire
            this.contactForm.subject = '';
            this.contactForm.priority = 'normal';
            this.contactForm.message = '';
            this.contactForm.consent = false;
            this.contactForm.senderName = '';

            // Reinitialiser la validation
            this.v$.$reset();
        },

        getTextLength(htmlContent: string): number {
            if (!htmlContent) return 0;
            // Creer un element temporaire pour extraire le texte sans HTML
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = htmlContent;
            return tempDiv.textContent?.length || 0;
        },

    }
});
</script>

<style scoped>
.field-group {
    margin-bottom: 1.5rem;
}

.error-message {
    color: #E74C3C;
    font-size: 0.875rem;
    margin-top: 0.25rem;
}

/* Styles pour les boutons radio */
.p-radiobutton .p-radiobutton-box {
    border-color: #DEE0E4;
}

.p-radiobutton .p-radiobutton-box.p-highlight {
    border-color: #00798C;
    background-color: #00798C;
}

/* Styles pour les checkboxes */
.p-checkbox .p-checkbox-box {
    border-color: #DEE0E4;
}

.p-checkbox .p-checkbox-box.p-highlight {
    border-color: #00798C;
    background-color: #00798C;
}

/* Styles pour l'editeur de texte */
.p-editor-toolbar {
    border-color: #DEE0E4;
    background: #F8F9FA;
    padding: 0.75rem;
}

.p-editor-content {
    border-color: #DEE0E4;
    min-height: 450px;
}

.p-editor-content .ql-editor {
    font-family: 'Poppins', sans-serif;
    font-size: 0.875rem;
    line-height: 1.6;
    padding: 1rem;
    min-height: 400px;
}

.p-editor-content .ql-editor.ql-blank::before {
    color: #757575;
    font-style: normal;
    content: 'Redigez votre message ici... Vous pouvez utiliser les outils de formatage ci-dessus.';
}

/* Styles pour l'editeur en erreur */
.p-editor.p-invalid .p-editor-toolbar,
.p-editor.p-invalid .p-editor-content {
    border-color: #E74C3C;
}

/* Amelioration de la zone d'ecriture */
.p-editor {
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.p-editor:focus-within {
    box-shadow: 0 4px 8px rgba(0, 121, 140, 0.15);
    transform: translateY(-1px);
    transition: all 0.3s ease;
}

/* Responsive */
@media (max-width: 768px) {
    .grid {
        grid-template-columns: 1fr;
    }

    .p-editor-toolbar {
        flex-wrap: wrap;
    }

    .p-editor-toolbar .ql-formats {
        margin-bottom: 0.5rem;
    }
}
</style>
